<template>
  <section class="h-full mx-4" id="customersManagement">
    <BaseTable
      v-if="customers"
      :headers="customerHeaders"
      :list="customers"
      :total-records="customersTotalCount"
      table-id="customers-management"
      :items-per-page="customersItemsPerPage"
      :selected-page="customersPageNumber"
      @refresh-data="onRefreshData"
      @deleteMultiselect="onDeleteMultiselect"
      @add-new="onAddNewCustomer"
      clickable
      @table-item-clicked="onItemClick"
      @apply-filters="onApplyFilters"
      @reset-filters="onResetFilters">
      <template #actions="{ item }">
        <div class="flex items-center justify-center gap-1">
          <ActionButton
            :isEnabled="item.isFromManager && !item.deleted"
            color="red"
            :icon="IconTrashOutline"
            @action="deleteSingleConfirm(item)" />
        </div>
      </template>

      <template #filters>
        <section class="flex flex-col items-start justify-start w-full max-w-xl py-4 mx-auto">
          <div>
            <label class="space-y-2 text-sm tracking-wide text-gray-400 uppercase">{{
              $t('baseObject.isActive')
            }}</label>
            <BaseTriStateToggle
              :label="$t('baseObject.isActive')"
              name="isActive"
              v-model="filterIsActive" />
          </div>
        </section>
      </template>
    </BaseTable>
    <BaseModalConfirm
      v-if="confirmDeleteSingleModalActive"
      @cancel="confirmDeleteSingleModalActive = false"
      @confirm="onDeleteSingle()">
      <template #title>{{ $t('common.actions.delete') }}</template>
      <template #text>
        {{ $t('messages.info.deleteSingle', { item: customerToDeleteSingle?.code }) }}</template
      >
      <template #buttonText>{{ $t('common.actions.delete') }}</template>
    </BaseModalConfirm>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { getCustomersWithConditions, removeCustomersByIds } from '@/services/api/traders';
import { useToastStore } from '@/stores/toast';
import BaseTable from '@/components/common/BaseTable.vue';
import BaseModalConfirm from '@/components/common/BaseModalConfirm.vue';
import BaseTriStateToggle from '@/components/common/BaseTriStateToggle.vue';
import type { Customer } from '@/types/Traders';
import type { QueryParams, TableFilterData } from '@/types/Base';
import router from '@/router';
import { v4 as uuidv4 } from 'uuid';
import ActionButton from '@/components/common/ActionButton.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import { useI18n } from 'vue-i18n';
import { useStateStore } from '@/stores/state';

const { t: $t } = useI18n({ useScope: 'global' });
const toastStore = useToastStore();
const customers = ref<Customer[]>();
const customersTotalCount = ref<number>();
const customersItemsPerPage = ref(50);
const customersPageNumber = ref(1);
const confirmDeleteSingleModalActive = ref(false);
const customerToDeleteSingle = ref<Customer>();
const filterIsActive = ref<boolean | undefined>(undefined);
const customerHeaders = [
  { value: 'code', text: `${$t('baseObject.code')}`, type: 'text', active: true },
  { value: 'trader.name', text: `${$t('customer.traderName')}`, type: 'text', active: true },
  {
    value: 'trader.taxNumber',
    text: `${$t('customer.traderTaxNumber')}`,
    type: 'text',
    active: true,
  },
  { value: 'trader.title', text: $t('customer.traderTitle'), type: 'text', active: true },
  { value: 'trader.address', text: $t('customer.traderAddress'), type: 'text', active: true },
  { value: 'trader.job', text: $t('customer.traderJob'), type: 'text', active: true },
  { value: 'trader.code', text: $t('customer.traderCode'), type: 'text', active: true },
  { value: 'trader.zipCode', text: $t('customer.traderZipCode'), type: 'text', active: true },
  {
    value: 'defaultPaymentType.name',
    text: $t('customer.defaultPaymentType'),
    type: 'text',
    active: true,
  },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
];

const { getTableSelectedPage } = useStateStore();

onMounted(async () => {
  const storedTableData = getTableSelectedPage('customers-management');
  if (storedTableData) {
    customersItemsPerPage.value = storedTableData.itemsPerPage;
    customersPageNumber.value = storedTableData.page;
  }
  await loadData({
    itemsPerPage: customersItemsPerPage.value,
    sortColumn: 'code',
    sortAscending: true,
    page: customersPageNumber.value,
  });
});

async function onRefreshData(data: TableFilterData) {
  const sortColumn = data.column !== undefined ? customerHeaders[data.column]?.value : 'code';
  const sortAscending = data.order === 'asc' || data.order === undefined;
  const params: QueryParams = {
    page: data.page || 1,
    itemsPerPage: data.itemsPerPage || 50,
    query: data.query || '',
    sortColumn: sortColumn,
    sortAscending: sortAscending,
    isActive: data.isActive,
  };

  await loadData(params);
}

async function loadData(params: QueryParams) {
  const res = await getCustomersWithConditions(params);
  if (res) {
    customers.value = res.data;
    customersTotalCount.value = res.count;
  }
}

async function onDeleteSingle() {
  if (!customerToDeleteSingle.value) return;
  confirmDeleteSingleModalActive.value = false;
  await deleteCustomers([customerToDeleteSingle.value.id]);
}

async function onDeleteMultiselect(items: Customer[]) {
  const idsToDelete = items.filter((x) => x.isFromManager == true).map((item) => item.id);
  if (idsToDelete.length) await deleteCustomers(idsToDelete);
}

async function deleteCustomers(idsToDelete: string[]) {
  const res = await removeCustomersByIds(idsToDelete);
  if (!res && customers.value)
    customers.value = customers.value.filter((type) => !idsToDelete.includes(type.id));
  res
    ? toastStore.error({ text: res })
    : toastStore.success({ text: $t('messages.success.successfullyDeleted') });
}

function deleteSingleConfirm(item: Customer) {
  customerToDeleteSingle.value = item;
  confirmDeleteSingleModalActive.value = true;
}

function onAddNewCustomer() {
  router.push({ name: 'customers-preview', params: { id: uuidv4() } });
}

function onItemClick(item: Customer) {
  router.push({ name: 'customers-preview', params: { id: item.id } });
}

function onApplyFilters(data: TableFilterData) {
  data.isActive = filterIsActive.value;
  onRefreshData(data);
}

function onResetFilters() {
  filterIsActive.value = undefined;
  onRefreshData({});
}
</script>
